import React, { useEffect, useState } from 'react';
import BrandLayout from '@/components/BrandLayout';
import { api } from '@/lib/api';
import { TrendingUp, Package, ShoppingBag, IndianRupee, FileText } from 'lucide-react';

const STATUS_LABEL = {
    placed: 'New', accepted: 'Accepted', packed: 'Packed', ready: 'Ready',
    out_for_delivery: 'Out for Delivery', delivered: 'Delivered', cancelled: 'Cancelled',
};

export default function BrandAnalytics() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get('/brand/analytics').then(({ data }) => setData(data)).catch(() => {});
    }, []);

    if (!data) return <BrandLayout><div className="p-10 text-text-secondary">Loading analytics…</div></BrandLayout>;

    const totalStatusCount = Object.values(data.by_status || {}).reduce((a, b) => a + b, 0) || 1;
    const maxMallGmv = Math.max(...(data.by_mall || []).map((m) => m.gmv), 1);

    return (
        <BrandLayout>
            <div className="p-10" data-testid="brand-analytics-page">
                <div className="mb-10">
                    <div className="overline">Deep Insights</div>
                    <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Analytics</h1>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <KPI label="Orders" value={data.summary.orders} icon={ShoppingBag} />
                    <KPI label="GMV" value={`₹${Number(data.summary.gmv || 0).toLocaleString('en-IN')}`} icon={IndianRupee} />
                    <KPI label="Items Sold" value={data.summary.items_sold} icon={Package} />
                    <KPI label="AOV" value={`₹${Math.round(data.summary.avg_order_value || 0).toLocaleString('en-IN')}`} icon={TrendingUp} />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-10">
                    <div className="bg-white border border-border-subtle rounded-md p-8" data-testid="orders-by-status">
                        <div className="overline mb-6">Orders by Status</div>
                        <div className="space-y-4">
                            {Object.entries(data.by_status || {}).map(([s, c]) => (
                                <div key={s}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-inorbit-blue">{STATUS_LABEL[s] || s}</span>
                                        <span className="text-text-tertiary">{c}</span>
                                    </div>
                                    <div className="h-1.5 bg-stone rounded-full overflow-hidden">
                                        <div className="h-full bg-inorbit-orange" style={{ width: `${(c / totalStatusCount) * 100}%` }} />
                                    </div>
                                </div>
                            ))}
                            {!Object.keys(data.by_status || {}).length && <div className="text-sm text-text-tertiary">No orders yet.</div>}
                        </div>
                    </div>
                    <div className="bg-white border border-border-subtle rounded-md p-8" data-testid="orders-by-mall">
                        <div className="overline mb-6">GMV by Mall</div>
                        <div className="space-y-4">
                            {(data.by_mall || []).map((m) => (
                                <div key={m._id}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-inorbit-blue">{m.mall_name}</span>
                                        <span className="text-text-tertiary">₹{Number(m.gmv).toLocaleString('en-IN')} ({m.orders})</span>
                                    </div>
                                    <div className="h-1.5 bg-stone rounded-full overflow-hidden">
                                        <div className="h-full bg-inorbit-blue" style={{ width: `${(m.gmv / maxMallGmv) * 100}%` }} />
                                    </div>
                                </div>
                            ))}
                            {!(data.by_mall || []).length && <div className="text-sm text-text-tertiary">No mall data yet.</div>}
                        </div>
                    </div>
                </div>

                <div className="bg-inorbit-blue text-white rounded-md p-8" data-testid="offline-bills-card">
                    <div className="flex items-center gap-3 mb-6">
                        <FileText size={20} className="text-inorbit-yellow" strokeWidth={1.4} />
                        <div className="overline text-inorbit-yellow">Offline Bills (OCR captured)</div>
                    </div>
                    <div className="grid grid-cols-3 gap-8">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/60">Bills</div>
                            <div className="font-serif text-4xl text-white mt-2">{data.offline_bills.bills}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/60">Captured</div>
                            <div className="font-serif text-4xl text-inorbit-yellow mt-2">₹{Number(data.offline_bills.captured || 0).toLocaleString('en-IN')}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/60">Points Issued</div>
                            <div className="font-serif text-4xl text-white mt-2">{Number(data.offline_bills.points || 0).toLocaleString('en-IN')}</div>
                        </div>
                    </div>
                </div>
            </div>
        </BrandLayout>
    );
}

function KPI({ label, value, icon: Icon }) {
    return (
        <div className="bg-white p-6 border border-border-subtle rounded-md">
            <Icon size={18} strokeWidth={1.4} className="text-text-tertiary" />
            <div className="font-serif text-3xl text-inorbit-blue mt-3 break-all">{value}</div>
            <div className="text-[11px] uppercase tracking-widest text-text-tertiary mt-1">{label}</div>
        </div>
    );
}
